import React from 'react';
import {observer} from 'mobx-react';
import s from '../s';
import {Portal} from '../../util';
import {GLASS_BENEFITS, GLASS_PHOTO, GLASS_TAG, GLASS_TAG_ID, glassPhotoLeftId, glassPhotoRightLabel, glassTitle} from './ab-glass-options';
import './ab-glass-options.scss';

type Props = {
    /** The glass ids this product offers (offeredGlassIds), in id order. */
    offered: number[];
    /** The control's save(id): sets s.ps.glass_id and re-prices. */
    onSelect: (id: number) => void;
};

/**
 * Test arm of the glass options test (ab-glass-options.ts): one radio box per glass option the product offers (title, this
 * product's price, benefit line), the "Most chosen upgrade" tag on UV & Reflection Control, then the glare comparison photo
 * when a reflection-control option is on offer; its left side is Conservation UV while that is selected, otherwise
 * Standard (glassPhotoLeftId). glass.tsx renders it in place of the control's .glass-prod tiles; the
 * header, modals and .glass-info block around it stay the control's. A native radio in each label gives tap-anywhere
 * selection, arrow-key movement and the box's text as the radio's name.
 */
const GlassOptions: React.FC<Props> = observer(({offered, onSelect}) => {
    const right = glassPhotoRightLabel(offered);
    const leftId = glassPhotoLeftId(s.ps.glass_id);
    const left = glassTitle(leftId);

    return (
        <>
            <div
                className="ab-glass-options"
                role="radiogroup"
                aria-label="Select frame glass"
            >
                {s.glass
                    .filter(m => offered.includes(m.id))
                    .map(m => {
                        const selected = s.ps.glass_id === m.id;
                        return (
                            <label
                                key={m.id}
                                className={`ab-glass-option${selected ? ' selected' : ''}`}
                                data-glass-id={m.id}
                            >
                                <input
                                    type="radio"
                                    name="ab-glass-option"
                                    value={m.id}
                                    checked={selected}
                                    onChange={() => onSelect(m.id)}
                                />
                                <span className="ab-glass-body">
                                    {m.id === GLASS_TAG_ID && <span className="ab-glass-tag">{GLASS_TAG}</span>}
                                    <span className="ab-glass-row">
                                        <span className="ab-glass-title">
                                            {m.title}
                                            {s.__fast_ship_orig && s.isRetail && m.id === 1 && (
                                                <span className={`prod-fast-shipping fast-shipping-${Portal?.PortalID}`}>
                                                    <i className={Portal?.PortalID !== 3 ? 'fas fa-shipping-fast' : 'fas fa-cart-shopping-fast'}></i>
                                                </span>
                                            )}
                                        </span>
                                        <span className="ab-glass-price">{m.price}</span>
                                    </span>
                                    <span className="ab-glass-benefit">{GLASS_BENEFITS[m.id]}</span>
                                </span>
                            </label>
                        );
                    })}
            </div>
            {right && (
                <div
                    className={`ab-glass-photo${leftId === 3 ? ' uv' : ''}`}
                    role="img"
                    aria-label={`Glare comparison: ${left} glass on the left shows reflections, ${right} on the right does not`}
                >
                    <picture>
                        <source
                            type="image/webp"
                            srcSet={`https://${Portal?.CDNDomain}/${GLASS_PHOTO}.webp`}
                        />
                        <img
                            src={`https://${Portal?.CDNDomain}/${GLASS_PHOTO}.jpg`}
                            alt=""
                            loading="lazy"
                        />
                    </picture>
                    <span
                        className="ab-glass-photo-label left"
                        aria-hidden="true"
                    >
                        {left}
                    </span>
                    <span
                        className="ab-glass-photo-label right"
                        aria-hidden="true"
                    >
                        {right}
                    </span>
                </div>
            )}
        </>
    );
});

export default GlassOptions;
